/**
 * G5 (AC-GP-012) — one press = one request.
 *
 * A resolver action starts a real, billable-adjacent server call. The button is
 * disabled while its request is in flight AND a ref guard rejects a second press
 * that lands in the same tick (a double click within 1 s makes exactly one call),
 * because a disabled attribute alone is a race the DOM can lose.
 *
 * The outcome — success OR the server's own refusal — is kept here so the surface
 * can show it inline; the owner UX rule is that a refusal is never silent.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { StrategyResolveOutcome } from '../services/strategyTaskResolve';

export interface TaskResolveActionState {
  /** The kind currently in flight (`'save-again'`, `'generate-again'`), or null. */
  running: string | null;
  outcome: StrategyResolveOutcome | null;
  run: (kind: string, action: () => Promise<StrategyResolveOutcome>) => void;
}

export function useTaskResolveAction(): TaskResolveActionState {
  const [running, setRunning] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<StrategyResolveOutcome | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const run = useCallback((kind: string, action: () => Promise<StrategyResolveOutcome>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRunning(kind);
    setOutcome(null);
    void action()
      .then((result) => { if (mounted.current) setOutcome(result); })
      .catch((error: unknown) => {
        if (mounted.current) {
          setOutcome({ severity: 'error', lines: [error instanceof Error ? error.message : String(error)] });
        }
      })
      .finally(() => {
        inFlight.current = false;
        if (mounted.current) setRunning(null);
      });
  }, []);

  return { running, outcome, run };
}
